import API_CONFIG from '../../config/apiConfig';

// Task Event Types
export interface TaskEventData {
  taskId: string;
  userId: string;
  organizationId: string;
  eventType: 'task_created' | 'task_updated' | 'task_deleted' | 'task_status_changed' | 'task_assigned';
  data: any;
  timestamp: string;
}

export interface TaskEvent {
  type: 'connection_established' | 'heartbeat' | 'task_created' | 'task_updated' | 'task_deleted' | 'task_status_changed' | 'task_assigned';
  data: TaskEventData | { clientId: string; timestamp: string };
}

export type TaskEventListener = (event: TaskEvent) => void;

class TaskEventService {
  private eventSource: EventSource | null = null;
  private listeners: Set<TaskEventListener> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnected = false;
  private isReconnecting = false;

  private getAuthToken(): string | null {
    // Use same priority as auth store: authToken first, then token
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  }

  /**
   * Connect to the task events SSE stream
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.eventSource && this.isConnected) {
        console.log('[TaskEventService] Already connected');
        resolve();
        return;
      }

      const token = this.getAuthToken();
      if (!token) {
        const error = new Error('No authentication token available');
        console.error('[TaskEventService] Connection failed:', error);
        reject(error);
        return;
      }

      try {
        console.log('[TaskEventService] Connecting to task events stream...');

        // Close existing connection if any
        this.disconnect();

        // Note: EventSource doesn't support custom headers directly
        // We need to pass the token as a query parameter for SSE
        const url = new URL(`${API_CONFIG.BASE_URL}/api/v1/tasks/events`);

        // Create EventSource with token in URL since headers aren't supported
        // For production, consider implementing a token endpoint or using cookies
        this.eventSource = new EventSource(url.toString());

        // Set up auth header using a custom approach
        // Since EventSource doesn't support custom headers, we'll need to handle auth differently
        this.setupEventHandlers();

        // Wait for connection to be established
        const connectionTimeout = setTimeout(() => {
          reject(new Error('Connection timeout'));
        }, 10000);

        const onConnected = () => {
          clearTimeout(connectionTimeout);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          console.log('[TaskEventService] Connected successfully');
          resolve();
        };

        const onError = () => {
          clearTimeout(connectionTimeout);
          if (!this.isReconnecting) {
            reject(new Error('Failed to connect to task events stream'));
          }
        };

        this.eventSource.addEventListener('open', onConnected);
        this.eventSource.addEventListener('error', onError);

      } catch (error) {
        console.error('[TaskEventService] Connection error:', error);
        reject(error);
      }
    });
  }

  /**
   * Disconnect from the task events stream
   */
  disconnect(): void {
    if (this.eventSource) {
      console.log('[TaskEventService] Disconnecting...');
      this.eventSource.close();
      this.eventSource = null;
      this.isConnected = false;
      this.isReconnecting = false;
    }
  }

  /**
   * Add event listener for task events
   */
  addListener(listener: TaskEventListener): void {
    this.listeners.add(listener);
  }

  /**
   * Remove event listener
   */
  removeListener(listener: TaskEventListener): void {
    this.listeners.delete(listener);
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): { connected: boolean; reconnecting: boolean } {
    return {
      connected: this.isConnected,
      reconnecting: this.isReconnecting
    };
  }

  /**
   * Setup event handlers for SSE connection
   */
  private setupEventHandlers(): void {
    if (!this.eventSource) return;

    // Handle connection open
    this.eventSource.onopen = () => {
      console.log('[TaskEventService] SSE connection opened');
      this.isConnected = true;
      this.reconnectAttempts = 0;
    };

    // Handle incoming messages
    this.eventSource.onmessage = (event) => {
      try {
        const taskEvent: TaskEvent = JSON.parse(event.data);
        console.log('[TaskEventService] Received event:', taskEvent.type, taskEvent.data);

        // Notify all listeners
        this.listeners.forEach(listener => {
          try {
            listener(taskEvent);
          } catch (error) {
            console.error('[TaskEventService] Error in event listener:', error);
          }
        });
      } catch (error) {
        console.error('[TaskEventService] Error parsing event data:', error);
      }
    };

    // Handle connection errors
    this.eventSource.onerror = (error) => {
      console.error('[TaskEventService] SSE connection error:', error);
      this.isConnected = false;

      // Only attempt reconnection if we haven't exceeded max attempts
      if (this.reconnectAttempts < this.maxReconnectAttempts && !this.isReconnecting) {
        this.attemptReconnect();
      } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('[TaskEventService] Max reconnection attempts reached');
        this.disconnect();
      }
    };

    // Handle specific event types
    this.eventSource.addEventListener('connection_established', (event) => {
      console.log('[TaskEventService] Connection established:', event.data);
    });

    this.eventSource.addEventListener('heartbeat', (event) => {
      // Heartbeat events help keep the connection alive
      // console.log('[TaskEventService] Heartbeat received');
    });
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  private attemptReconnect(): void {
    if (this.isReconnecting) return;

    this.isReconnecting = true;
    this.reconnectAttempts++;

    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`[TaskEventService] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      if (this.isReconnecting) {
        this.connect()
          .then(() => {
            this.isReconnecting = false;
          })
          .catch(() => {
            this.isReconnecting = false;
            // The error will trigger another reconnection attempt if under max attempts
          });
      }
    }, delay);
  }

  /**
   * Force reconnect (useful for testing or manual reconnection)
   */
  reconnect(): Promise<void> {
    console.log('[TaskEventService] Force reconnecting...');
    this.disconnect();
    return this.connect();
  }
}

// Export singleton instance
export const taskEventService = new TaskEventService();